/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, RefreshControl, ScrollView, StyleSheet, Text, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { ShellContentFrame } from '../../shared/layout/ShellContentFrame'
import { useTabWorkspaceLayout } from '../../shared/layout/useContentLayout';
import { spacing } from '../../shared/theme/spacing';
import { ensureTicketFonts } from '../../shared/ticket/ensureTicketFonts'
import { PaperSheet } from '../../shared/ticket/PaperSheet'
import { TicketDeskFooter } from '../../shared/ticket/TicketDeskFooter'
import { ticketColors } from '../../shared/theme/ticketColors'
import { ticketSurfaces } from '../../shared/ticket/ticketSurfaces.styles';
import { ModuleEmptyState } from '../../shared/ui/ModuleEmptyState'
import { MobilePagination as StockMobilePagination } from '../../shared/ui/MobilePagination';
import { FilterSheetButton } from '../../shared/filters/FilterSheetButton';
import { PromotionHistoryModal } from '../components/PromotionHistoryModal';
import { PromotionFormModal } from '../components/PromotionFormModal';
import { PromotionRowCard } from '../components/PromotionRowCard';
import { PromotionsTable } from '../components/PromotionsTable';
import { PromotionsFiltersPanel } from '../components/PromotionsFiltersPanel';
import { PromotionsScreenHeader } from '../components/PromotionsScreenHeader';
import { PromotionsBatchBar } from '../components/PromotionsBatchBar';
import { PromotionConflictBanner } from '../components/PromotionConflictBanner';
import { PromotionTopPerformersCard } from '../components/PromotionTopPerformersCard';
import { useScreenLoadShell } from '../../shared/ui/hooks-screen/useScreenLoad';
import { usePromotionHistory } from '../application/usePromotionHistory';
import { usePromotionsListScreen } from '../application/usePromotionsListScreen';
import { usePromotionKpis } from '../application/usePromotionKpis';
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import { useConnectivityStatus } from '../../shell/infra-api';
import { TicketAlertStrip } from '../../shared/ticket/TicketAlertStrip';
import { formatMoneyCompact } from '../../shared/format/moneyFormat';
import { posSharedUi as posUi } from '../../shared/ui/posSharedUi';

export function PromotionsListScreen() {
  useEffect(() => {
    ensureTicketFonts();
  }, []);

  const vm = usePromotionsListScreen();
  const historyVm = usePromotionHistory(vm.historyPromotion);
  const currentBranchId = useSessionStore((state) => state.currentBranchId);
  const kpis = usePromotionKpis(currentBranchId);
  const insets = useSafeAreaInsets();
  const { layoutWide } = useTabWorkspaceLayout();
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  // El listado lee el snapshot RAM del catálogo: sin internet se muestra la
  // última foto guardada — avisar para que no parezca live.
  const connectivity = useConnectivityStatus();
  const showingCachedData = !connectivity.isOnline || !connectivity.isInternetReachable;

  const { refreshing, onRefresh } = useScreenLoadShell(
    'PromotionsList',
    async (opts) => {
      await Promise.all([vm.loadFn(opts), kpis.refresh()]);
    },
    { minFocusIntervalMs: 60_000 }
  );

  const filterTrigger = (
    <FilterSheetButton
      activeCount={vm.hasActiveFilters ? 1 : 0}
      onOpen={() => setFilterSheetOpen(true)}
      variant={layoutWide ? 'desk' : 'ticket'}
      style={layoutWide ? undefined : styles.filterTriggerMobile}
    />
  );

  const listContentStyle = useMemo(
    () => ({
      paddingHorizontal: spacing.md,
      paddingTop: spacing.sm,
      paddingBottom: insets.bottom + 16,
      flexGrow: vm.promotions.length === 0 ? 1 : undefined,
    }),
    [insets.bottom, vm.promotions.length]
  );

  const listRows = useMemo(() => {
    if (vm.promotions.length === 0) return null;
    if (vm.batchMode) {
      return (
        <PromotionsTable
          promotions={vm.promotions}
          canEdit={vm.canEdit}
          canActivate={vm.canActivate}
          onEdit={vm.onEdit}
          onToggleActive={vm.onToggleActive}
          onDuplicate={vm.onDuplicate}
          onOpenHistory={vm.onOpenHistory}
          sortKey={vm.sortKey}
          sortDir={vm.sortDir}
          onSortChange={vm.onSortChange}
          performanceByPromoId={kpis.performanceByPromoId}
          batchMode
          selectedIds={vm.selectedIds}
          onToggleSelection={vm.toggleSelection}
          onToggleSelectAll={vm.toggleSelectAll}
          embedded
        />
      );
    }
    return vm.promotions.map((promo, index) => {
      const group = index === vm.promotions.length - 1 ? 'groupedLast' : 'grouped';
      return (
        <PromotionRowCard
          key={promo.id}
          promo={promo}
          canEdit={vm.canEdit}
          canActivate={vm.canActivate}
          formatRowSubtitle={vm.formatRowSubtitle}
          onEdit={vm.onEdit}
          onToggleActive={vm.onToggleActive}
          onDuplicate={vm.onDuplicate}
          onOpenHistory={vm.onOpenHistory}
          onDelete={vm.onDelete}
          group={group}
        />
      );
    });
  }, [
    vm.promotions,
    vm.batchMode,
    vm.canEdit,
    vm.canActivate,
    vm.formatRowSubtitle,
    vm.onEdit,
    vm.onToggleActive,
    vm.onDelete,
    vm.onDuplicate,
    vm.onOpenHistory,
    vm.sortKey,
    vm.sortDir,
    vm.onSortChange,
    vm.selectedIds,
    vm.toggleSelection,
    vm.toggleSelectAll,
    kpis.performanceByPromoId,
  ]);

  const perfSummary = useMemo(() => {
    if (kpis.unavailable && !kpis.loading) {
      return (
        <Text style={styles.perfSummaryText}>
          {kpis.unavailableMessage ?? 'Las métricas de uso todavía no están disponibles.'}
        </Text>
      );
    }
    const perf = kpis.performanceKpis;
    if (!perf || (perf.totalUses === 0 && perf.totalDiscount === 0)) return null;
    return (
      <Text style={styles.perfSummaryText}>
        30 días: {perf.totalUses} usos · {formatMoneyCompact(perf.totalDiscount)} de descuento
      </Text>
    );
  }, [kpis.performanceKpis, kpis.unavailable, kpis.unavailableMessage, kpis.loading]);

  const topPerformers = useMemo(() => {
    const perf = kpis.performanceKpis;
    if (!perf || perf.topPromotions.length === 0) return null;
    return <PromotionTopPerformersCard topPromotions={perf.topPromotions} />;
  }, [kpis.performanceKpis]);

  const emptyList = vm.loadError ? (
    <ModuleEmptyState
      title="No se pudieron cargar las promos"
      subtitle={vm.loadError}
      primaryAction={{ label: 'Reintentar', onPress: () => void vm.loadFn({ force: true }) }}
      testID="promotions.empty"
    />
  ) : (
    <ModuleEmptyState
      title={vm.totalCount === 0 ? 'Sin promociones' : 'Sin resultados'}
      subtitle={
        vm.totalCount === 0
          ? vm.canEdit
            ? 'Armá un 2×1 en dos toques y probalo en el mostrador.'
            : 'Todavía no hay promos en este negocio.'
          : 'Probá otro filtro o limpiá la búsqueda.'
      }
      primaryAction={
        vm.totalCount === 0 && vm.canEdit
          ? {
              // Mismo verbo que el CTA del header (#1459).
              label: 'Nueva promoción',
              onPress: vm.onCreate,
              testID: 'promotions.empty.new',
            }
          : undefined
      }
      secondaryAction={
        vm.totalCount === 0 && vm.canEdit
          ? {
              label: 'Crear promo ejemplo',
              onPress: vm.onCreateExample,
              testID: 'promotions.empty.create-example',
            }
          : vm.hasActiveFilters && vm.filteredTotalCount === 0 && vm.totalCount > 0
            ? { label: 'Limpiar filtros', onPress: vm.clearFilters }
            : undefined
      }
      testID="promotions.empty"
    />
  );

  const listPagination =
    vm.filteredTotalCount > 0 ? (
      layoutWide ? (
        <TicketDeskFooter
          pageIndex={vm.pageIndex}
          pageSize={vm.pageSize}
          productCount={vm.promotions.length}
          totalCount={vm.filteredTotalCount}
          hasMore={vm.hasMore}
          loading={vm.loading}
          onPreviousPage={vm.goToPreviousPage}
          onNextPage={vm.goToNextPage}
          embedded
        />
      ) : (
        <StockMobilePagination
          pageIndex={vm.pageIndex}
          pageSize={vm.pageSize}
          totalCount={vm.filteredTotalCount}
          productCount={vm.promotions.length}
          loading={vm.loading}
          hasMore={vm.hasMore}
          onPreviousPage={vm.goToPreviousPage}
          onNextPage={vm.goToNextPage}
        />
      )
    ) : null;

  if (!vm.canView) {
    return (
      <ShellContentFrame style={ticketSurfaces.screen}>
        <ModuleEmptyState
          title="Sin permiso"
          subtitle="No tenés permiso para ver promos en este local. Pedile al dueño que te habilite Promociones."
          testID="promotions.no-permission"
        />
      </ShellContentFrame>
    );
  }

  return (
    <>
      <ShellContentFrame style={ticketSurfaces.screen}>
        <ScrollView
          style={styles.list}
          contentContainerStyle={listContentStyle}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={ticketColors.gold} />
          }
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
        >
          <View style={styles.stack}>
                {/* Header + KPIs */}
                <PromotionsScreenHeader
                  activeCount={vm.listSummary.activeCount}
                  expiredCount={vm.listSummary.expiredCount}
                  programmedCount={vm.listSummary.programmedCount}
                  totalCount={vm.totalCount}
                  loadError={vm.loadError}
                  showReadonlyBanner={vm.canView && !vm.canEdit}
                  search={vm.search}
                  onSearchChange={vm.setSearch}
                  canEdit={vm.canEdit}
                  canExport={vm.canExport}
                  canEnterBatch={vm.canActivate}
                  onCreate={vm.onCreate}
                  onExportCsv={() => void vm.onExportCsv()}
                  onEnterBatchMode={vm.canEdit ? vm.enterBatchMode : undefined}
                  filterTrigger={filterTrigger}
                  hideMetrics={
                    vm.totalCount === 0 && !vm.hasActiveFilters && vm.search.trim() === ''
                  }
                />

                {/* Filtros: trigger vive en el header; el panel solo monta el sheet */}
                <PromotionsFiltersPanel
                  filter={vm.filter}
                  onFilterChange={vm.setFilter}
                  hasActiveFilters={vm.hasActiveFilters}
                  onClearFilters={vm.clearFilters}
                  hideTrigger
                  sheetOpen={filterSheetOpen}
                  onSheetOpenChange={setFilterSheetOpen}
                />

                {/* Batch bar */}
                {vm.batchMode ? (
                  <PromotionsBatchBar
                    selectedCount={vm.selectedCount}
                    canActivate={vm.canActivate}
                    onActivateAll={() => void vm.onBatchToggleActive(true)}
                    onDeactivateAll={() => void vm.onBatchToggleActive(false)}
                    onClear={vm.clearSelection}
                    onExit={vm.exitBatchMode}
                  />
                ) : null}

                {/* Alert: snapshot cacheado sin conexión */}
                {showingCachedData && !vm.loading ? (
                  <TicketAlertStrip
                    title="Sin conexión"
                    subtitle="Mostrando los datos guardados en esta compu."
                    tone="warn"
                    hideChevron
                    testID="promotions.alert.offline"
                    accessibilityLabel="Sin conexión: mostrando datos guardados"
                  />
                ) : null}

                {/* Alert: conflictos */}
                <PromotionConflictBanner conflicts={vm.conflicts} />

                {/* Perf summary */}
                {perfSummary ? <View style={styles.perfSummaryRow}>{perfSummary}</View> : null}

                {/* Body paper: lista Square o empty */}
                <PaperSheet style={styles.listSheet} testID="promotions.list.sheet">
                  {vm.loading && vm.promotions.length === 0 ? (
                    <View style={styles.listLoading}>
                      <ActivityIndicator color={ticketColors.gold} />
                      <Text style={posUi.caption}>Cargando promociones…</Text>
                    </View>
                  ) : (
                    <>
                      {listRows}
                      {vm.promotions.length === 0 ? emptyList : null}
                    </>
                  )}
                </PaperSheet>

                {/* Paginación */}
                {vm.promotions.length > 0 ? listPagination : null}

                {/* Insights: top performers */}
                {topPerformers ? <View style={styles.insightsWrap}>{topPerformers}</View> : null}
          </View>
        </ScrollView>
      </ShellContentFrame>

      <PromotionHistoryModal
        visible={vm.historyPromotion != null}
        promotionName={vm.historyPromotion?.name ?? ''}
        events={historyVm.rows}
        historyTotalCount={historyVm.totalCount}
        historyLoading={historyVm.loading}
        historyLoadingMore={historyVm.loadingMore}
        historyHasMore={historyVm.hasMore}
        onLoadMore={historyVm.onLoadMore}
        onClose={vm.onCloseHistory}
        actorLabel={historyVm.actorLabel}
        eventOptions={historyVm.eventOptions}
        eventFilter={historyVm.eventFilter}
        onEventFilterChange={(value) => historyVm.setEventFilter(value as typeof historyVm.eventFilter)}
        fromDate={historyVm.fromDate}
        toDate={historyVm.toDate}
        onFromDateChange={historyVm.setFromDate}
        onToDateChange={historyVm.setToDate}
        onApplyFilters={historyVm.onApplyFilters}
      />

      <PromotionFormModal
        key={`${vm.editingPromotionId ?? vm.duplicateFromId ?? vm.createTemplateId ?? 'new'}-${vm.formModalVisible}`}
        visible={vm.formModalVisible}
        promotionId={vm.editingPromotionId}
        duplicateFromId={vm.duplicateFromId}
        createTemplateId={vm.createTemplateId}
        onCheckDraftConflicts={vm.checkDraftConflicts}
        onClose={() => {
          vm.onCloseFormModal();
          void kpis.refresh();
        }}
      />
    </>
  );
}

const styles = StyleSheet.create({
  list: {
    flex: 1,
  },
  stack: {
    gap: 10,
  },
  listSheet: {
    overflow: 'hidden',
  },
  perfSummaryRow: {
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.sm,
    borderRadius: 10,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
  },
  perfSummaryText: {
    fontSize: 12,
    fontWeight: '600',
    color: ticketColors.ink2,
  },
  insightsWrap: {
    marginTop: spacing.sm,
  },
  filterTriggerMobile: {
    flex: 1,
    marginBottom: 0,
  },
  listLoading: {
    paddingVertical: spacing.xl,
    alignItems: 'center',
    gap: spacing.sm,
  },
});
